import { Fragment, useId, type ReactNode } from "react";
import { patents, type PatentId, type PatentRecord } from "../../data/patents";
import styles from "./CartridgeHover.module.css";

export const CARTRIDGE_NAME = "7×57mm Mauser";

type Props = {
  variant?: "inline" | "stat";
};

export function CartridgeHover({ variant = "inline" }: Props) {
  const blurbId = useId();
  return (
    <span className={`${styles.wrap} ${variant === "stat" ? styles.stat : ""}`}>
      <button type="button" className={styles.trigger} aria-describedby={blurbId}>
        {CARTRIDGE_NAME}
      </button>
      <span className={styles.panel} id={blurbId} role="tooltip">
        <b>7×57mm Mauser</b>
        <span>
          Mexico’s 7mm service round — also called 7mm Mauser. Rimless bottleneck case, 57 mm long,
          about 78 mm overall. The M1908 Mondragón was adopted in this cartridge. Also attempted
          7.92×57; Swiss trials in 7.5×55. German conversion of leftover rifles to 7.92×57mm failed.
        </span>
        <span className={styles.pics}>
          <img src="/assets/art/ammo/7x57-cartridge.jpg" alt="7×57mm Mauser cartridge" />
          <img src="/assets/art/ammo/7x57-schematic.png" alt="7×57mm Mauser dimensional schematic" />
        </span>
      </span>
    </span>
  );
}

type NoteHoverProps = {
  children: ReactNode;
  title?: string;
  body: string;
  variant?: "inline" | "stat";
};

export function NoteHover({ children, title, body, variant = "inline" }: NoteHoverProps) {
  const blurbId = useId();
  return (
    <span className={`${styles.wrap} ${variant === "stat" ? styles.stat : ""}`}>
      <button type="button" className={styles.trigger} aria-describedby={blurbId}>
        {children}
      </button>
      <span className={styles.panel} id={blurbId} role="tooltip">
        {title ? <b>{title}</b> : null}
        <span>{body}</span>
      </span>
    </span>
  );
}

function PatentHover({ patent }: { patent: PatentRecord }) {
  const blurbId = useId();
  return (
    <span className={`${styles.wrap} ${styles.patent}`}>
      <button type="button" className={styles.trigger} aria-describedby={blurbId}>
        {patent.label}
      </button>
      <span className={styles.panel} id={blurbId} role="tooltip">
        <b>{patent.label}</b>
        <span className={styles.meta}>
          {patent.title} · {patent.office} · {patent.issued}
        </span>
        <span>{patent.blurb}</span>
        {patent.images.length > 0 ? (
          <span className={`${styles.pics} ${patent.images.length === 1 ? styles.picsOne : ""}`}>
            {patent.images.map((img) => (
              <img key={img.src} src={img.src} alt={img.alt} />
            ))}
          </span>
        ) : null}
        <a className={styles.registry} href={patent.href} target="_blank" rel="noreferrer">
          Open in the public patent registry
        </a>
      </span>
    </span>
  );
}

const RICH = /(\{patent:[A-Z0-9]+\}|7×57mm Mauser)/g;

export function HistoryParagraph({ text }: { text: string }) {
  const parts = text.split(RICH);
  if (parts.length === 1) return <p>{text}</p>;
  return (
    <p>
      {parts.map((part, i) => {
        if (part === CARTRIDGE_NAME) return <CartridgeHover key={i} />;
        const token = part.match(/^\{patent:([A-Z0-9]+)\}$/);
        if (token) {
          const rec = patents[token[1] as PatentId];
          return rec ? <PatentHover key={i} patent={rec} /> : <Fragment key={i}>{part}</Fragment>;
        }
        return <Fragment key={i}>{part}</Fragment>;
      })}
    </p>
  );
}
